import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import SearchBar from '../components/SearchBar'
import { TrendingUp, Users, DollarSign, Building2, Car, ArrowUp, ArrowDown, ArrowLeft, ChevronsUpDown, ChevronRight, Layers } from 'lucide-react'
import { Pie } from 'react-chartjs-2'
import { ColumnDefinition } from '../utils/exportToCsv'
import LoanWeeklyChart from '../components/LoanWeeklyChart'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'
// import PeriodInfoBanner from '../components/PeriodInfoBanner'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { toLocalDateStr } from '../utils/dateUtils'
import { useMaxDataDate, useMaxLoanDate } from '../hooks/useMaxDataDate'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { localizeApiDetail } from '../i18n/apiError'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

// Helper function to safely parse and format numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

const formatDecimal = (value: number | string | null | undefined, decimals: number = 1): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

interface LoanOverview {
  total_vehicles: number
  vehicles_with_loan: number
  loan_percentage: number
  avg_vehicle_age_with_loan: number
  avg_vehicle_age_without_loan: number
  avg_owner_age_with_loan: number
  avg_owner_age_without_loan: number
  top_brands_by_loan_share: BrandLoanStats[]
}

interface BrandLoanStats {
  brand: string
  total_registrations: number
  vehicles_with_loan: number
  loan_percentage: number | string
  private_loan_count: number
  business_loan_count: number
  ev_count: number | string
  ev_percentage: number | string
}

interface ModelLoanStats {
  brand: string
  model: string
  total_registrations: number
  vehicles_with_loan: number
  loan_percentage: number | string
  avg_vehicle_age: number | string | null
  avg_owner_age: number | string | null
  avg_owner_age_with_loan: number | string | null
  avg_owner_age_without_loan: number | string | null
}

interface BankStats {
  bank: string
  total_loans: number
  market_share: number | string
  top_brands: Array<{ brand: string; count: number }>
}

export default function LoanAnalytics() {
  const { t, i18n } = useTranslation('leasingLoans')
  // Period label for the explanation box; mirrors DateFilterBar's OUTPUT_MODE_LABELS.
  // The persisted mode can be one DateFilterBar doesn't know (e.g. last_week from Aldersanalyse).
  const periodLabel = (mode: OutputMode) =>
    i18n.exists(`leasingLoans:outputModes.${mode}`) ? t(`outputModes.${mode}` as const) : t('periodFallback')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'models' | 'banks'>('brands')
  
  // Overview data
  const [overview, setOverview] = useState<LoanOverview | null>(null)
  const [weeklyData, setWeeklyData] = useState<any[]>([])
  const [prevYearWeeklyData, setPrevYearWeeklyData] = useState<any[]>([])
  
  // Brand & Model data
  const [brands, setBrands] = useState<BrandLoanStats[]>([])
  const [brandsSummary, setBrandsSummary] = useState<any>(null)
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)
  const [brandSearchTerm, setBrandSearchTerm] = useState('')
  const [models, setModels] = useState<ModelLoanStats[]>([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [brandModelsSearch, setBrandModelsSearch] = useState('')
  const [brandModelsSortConfig, setBrandModelsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'vehicles_with_loan', direction: 'desc' })

  // Brand sort state - default: sort by vehicles with loan count, descending
  const [brandSortConfig, setBrandSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'vehicles_with_loan', direction: 'desc' })

  // All models data (Modeller tab)
  const [allModels, setAllModels] = useState<ModelLoanStats[]>([])
  const [allModelsLoading, setAllModelsLoading] = useState(false)
  const [allModelsLoaded, setAllModelsLoaded] = useState(false)
  const [allModelsError, setAllModelsError] = useState<string | null>(null)
  const [allModelsSearch, setAllModelsSearch] = useState('')
  const [modelSortConfig, setModelSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'vehicles_with_loan', direction: 'desc' })

  // Bank data
  const [banks, setBanks] = useState<BankStats[]>([])
  const [selectedBrands, setSelectedBrands] = useState<string[]>([])
  const [availableBrands, setAvailableBrands] = useState<string[]>([])
  
  // Date range state from year context - loan data is available up to 15 days ago
  const { startDate, setStartDate, endDate: contextEndDate, setEndDate, selectedYear } = useYearDates('loan-analytics')
  
  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()

  // Max data dates
  const maxDataDate = useMaxDataDate()
  const maxLoanDate = useMaxLoanDate()
  
  // Create adjusted end date for loan data based on actual BrregLoan freshness
  // Fall back to today - 15 days while the hook is loading
  const today = new Date()
  const fallbackDate = new Date(today)
  fallbackDate.setDate(today.getDate() - 15)
  const loanCutoff = maxLoanDate ? new Date(maxLoanDate + 'T00:00:00') : fallbackDate
  
  // End date should be the actual loan data cutoff, but not beyond the selected year's end
  const yearEnd = new Date(contextEndDate)
  let adjustedEndDate = loanCutoff < yearEnd ? loanCutoff : yearEnd
  
  // Ensure end date is never before start date
  adjustedEndDate = adjustedEndDate < startDate ? startDate : adjustedEndDate
  const endDate = adjustedEndDate
  
  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('loan-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  // Mine merker highlighting
  const { activeMerker } = useMineMerker()

  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')

  // Queried-state tracking — only update when fetchData actually runs
  const [queriedOutputMode, setQueriedOutputMode] = useState<OutputMode>(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)
  const [queriedStartDate, setQueriedStartDate] = useState(toLocalDateStr(startDate))
  const [queriedEndDate, setQueriedEndDate] = useState(toLocalDateStr(endDate))
  const [queriedSelectedBrand, setQueriedSelectedBrand] = useState<string | null>(selectedBrand)
  const [queriedSelectedBrands, setQueriedSelectedBrands] = useState<string[]>(selectedBrands)

  // Single effect for all fetch triggers. Waits for maxLoanDate before first fetch.
  useEffect(() => {
    if (!maxLoanDate) return
    handleApplyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedYear, vehicleFilter, maxLoanDate, outputMode])

  // Fetch available brands on mount
  useEffect(() => {
    fetchAvailableBrands()
  }, [])

  // Re-fetch when postal code filter changes
  useEffect(() => {
    if (!maxLoanDate) return
    if (postnummerFilterEnabled || minePostnummer !== null) {
      setAllModelsLoaded(false)
      if (activeTab === 'models') {
        fetchAllModels()
      } else {
        fetchData()
      }
    }
  }, [minePostnummer, postnummerFilterEnabled])

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('loan.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  const handleApplyFilters = () => {
    let computedStart: string
    let computedEnd: string

    if (outputMode === 'custom') {
      if (!customStartDate || !customEndDate) return
      const s = new Date(customStartDate)
      const e = new Date(customEndDate)
      if (!validateDateRange(s, e)) return
      setStartDate(s)
      setEndDate(e)
      computedStart = customStartDate
      computedEnd = customEndDate
    } else {
      const maxDate = maxLoanDate ? new Date(maxLoanDate + 'T00:00:00') : null
      const range = getDateRangeForMode(outputMode, maxDate)
      setStartDate(new Date(range.startDate))
      setEndDate(new Date(range.endDate))
      setDateRangeAlert(null)
      computedStart = range.startDate
      computedEnd = range.endDate
    }
    if (activeTab === 'models') fetchAllModels(computedStart, computedEnd)
    else fetchData(computedStart, computedEnd)
  }


  const fetchAvailableBrands = async () => {
    try {
      const response = await publicAPI.getBrands()
      const brandNames: string[] = response.data?.brands ?? []
      setAvailableBrands(brandNames)
    } catch (err) {
      console.error('Failed to fetch brands:', err)
      // Fallback to default list
      setAvailableBrands(['Tesla', 'BMW', 'Audi', 'Mercedes-Benz', 'Volkswagen', 'Toyota', 'Volvo'])
    }
  }

  const fetchData = async (startOverride?: string, endOverride?: string) => {
    try {
      setLoading(true)
      setError(null)

      const start_date = startOverride ?? toLocalDateStr(startDate)
      const end_date = endOverride ?? toLocalDateStr(endDate)

      // Snapshot queried state
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(start_date)
      setQueriedEndDate(end_date)
      setQueriedSelectedBrand(selectedBrand)
      setQueriedSelectedBrands([...selectedBrands])
      
      const varebilParam = getVarebilParam()

      const params: any = { start_date, end_date }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }
      
      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Fetch data based on active tab
      if (activeTab === 'overview') {
        const weeklyParams: any = { year: parseInt(selectedYear) }
        if (varebilParam !== undefined) weeklyParams.varebil = varebilParam

        const [overviewRes, weeklyRes] = await Promise.all([
          publicAPI.getLoanOverview(params),
          publicAPI.getWeeklyTimeSeries(weeklyParams),
        ])
        setOverview(overviewRes.data.data)
        setWeeklyData(weeklyRes.data.data || [])
        setPrevYearWeeklyData(weeklyRes.data.prev_year_data || [])
      } else if (activeTab === 'brands') {
        const brandsRes = await publicAPI.getLoansByBrand({ ...params, limit: 100 })
        setBrands(brandsRes.data.data || [])
        setBrandsSummary(brandsRes.data.summary || null)
      } else if (activeTab === 'banks') {
        const bankParams: any = { 
          start_date, 
          end_date, 
          limit: 20 
        }
        if (varebilParam !== undefined) {
          bankParams.varebil = varebilParam
        }
        if (selectedBrands.length > 0) {
          bankParams.MerkeNavn = selectedBrands.join(',')
        }
        const banksRes = await publicAPI.getLoansByBank(bankParams)
        setBanks(banksRes.data.data || [])
      }
    } catch (err: any) {
      console.error('Loan analytics error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('loan.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleBrandClick = async (brand: string) => {
    setSelectedBrand(brand)
    setBrandModelsSearch('')
    setBrandModelsSortConfig({ key: 'vehicles_with_loan', direction: 'desc' })

    try {
      setModelsLoading(true)
      const start_date = toLocalDateStr(startDate)
      const end_date = toLocalDateStr(endDate)

      const params: any = { brand, start_date, end_date, limit: 200 }
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }
      const varebilParam = getVarebilParam()
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      const response = await publicAPI.getLoansByModel(params)
      setModels(response.data.data || [])
    } catch (err: any) {
      console.error('Error fetching models:', err)
    } finally {
      setModelsLoading(false)
    }
  }

  const handleBackToBrands = () => {
    setSelectedBrand(null)
    setModels([])
  }

  const fetchAllModels = async (startOverride?: string, endOverride?: string) => {
    try {
      setAllModelsLoading(true)
      setAllModelsError(null)
      const start_date = startOverride ?? toLocalDateStr(startDate)
      const end_date = endOverride ?? toLocalDateStr(endDate)

      // Snapshot queried state so the explanation box matches this fetch
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(start_date)
      setQueriedEndDate(end_date)

      const params: any = { start_date, end_date, limit: 200 }
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }
      const varebilParam = getVarebilParam()
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      const response = await publicAPI.getLoansByModel(params)
      setAllModels(response.data.data || [])
      setAllModelsLoaded(true)
    } catch (err: any) {
      console.error('Error fetching all models:', err)
      setAllModelsError(localizeApiDetail(err?.response?.data?.detail) || err?.message || t('loan.loadModelsError'))
    } finally {
      setAllModelsLoading(false)
    }
  }

  const handleTabSwitch = (tab: 'overview' | 'brands' | 'models' | 'banks') => {
    setActiveTab(tab)
    if (tab === 'models' && !allModelsLoaded) {
      fetchAllModels()
    }
  }

  // Error is shown inline within tabs, not as page-level early return

  // Brand sort handlers
  const handleBrandSort = (key: keyof BrandLoanStats) => {
    setBrandSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getBrandSortIcon = (key: keyof BrandLoanStats) => {
    if (!brandSortConfig || brandSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (brandSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  // Sorted brands with useMemo
  const sortedBrands = useMemo(() => {
    if (!brandSortConfig || !brandSortConfig.key || !brandSortConfig.direction) return brands

    return [...brands].sort((a, b) => {
      const key = brandSortConfig.key as keyof BrandLoanStats
      const aVal = a[key]
      const bVal = b[key]

      // Handle string sorting (brand name)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return brandSortConfig.direction === 'asc' ? cmp : -cmp
      }

      // Handle numeric sorting
      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      if (aNum === null && bNum === null) return 0
      if (aNum === null) return 1
      if (bNum === null) return -1

      return brandSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [brands, brandSortConfig])

  // Filtered brands by search term
  const filteredBrands = useMemo(() => {
    if (!brandSearchTerm) return sortedBrands
    return sortedBrands.filter(b =>
      b.brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
    )
  }, [sortedBrands, brandSearchTerm])

  // Brand drill-down model sort handlers
  const handleBrandModelSort = (key: keyof ModelLoanStats) => {
    setBrandModelsSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getBrandModelSortIcon = (key: keyof ModelLoanStats) => {
    if (!brandModelsSortConfig || brandModelsSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (brandModelsSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  const filteredBrandModels = useMemo(() => {
    if (!brandModelsSearch) return models
    const term = brandModelsSearch.toLowerCase()
    return models.filter(m => m.model.toLowerCase().includes(term))
  }, [models, brandModelsSearch])

  const sortedBrandModels = useMemo(() => {
    if (!brandModelsSortConfig || !brandModelsSortConfig.key || !brandModelsSortConfig.direction) return filteredBrandModels

    return [...filteredBrandModels].sort((a, b) => {
      const key = brandModelsSortConfig.key as keyof ModelLoanStats
      const aVal = a[key]
      const bVal = b[key]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return brandModelsSortConfig.direction === 'asc' ? cmp : -cmp
      }

      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      if (aNum === null && bNum === null) return 0
      if (aNum === null) return 1
      if (bNum === null) return -1

      return brandModelsSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [filteredBrandModels, brandModelsSortConfig])

  const brandModelsSummary = useMemo(() => {
    if (models.length === 0) return null
    const totalReg = models.reduce((s, m) => s + m.total_registrations, 0)
    const totalLoan = models.reduce((s, m) => s + m.vehicles_with_loan, 0)
    return {
      total_registrations: totalReg,
      vehicles_with_loan: totalLoan,
      loan_percentage: totalReg > 0 ? (totalLoan / totalReg) * 100 : 0,
    }
  }, [models])

  // Model sort handlers (Modeller tab)
  const handleModelSort = (key: keyof ModelLoanStats) => {
    setModelSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getModelSortIcon = (key: keyof ModelLoanStats) => {
    if (!modelSortConfig || modelSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (modelSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  // Filtered and sorted all-models
  const filteredAllModels = useMemo(() => {
    if (!allModelsSearch) return allModels
    const term = allModelsSearch.toLowerCase()
    return allModels.filter(m =>
      m.brand.toLowerCase().includes(term) || m.model.toLowerCase().includes(term)
    )
  }, [allModels, allModelsSearch])

  const sortedAllModels = useMemo(() => {
    if (!modelSortConfig || !modelSortConfig.key || !modelSortConfig.direction) return filteredAllModels

    return [...filteredAllModels].sort((a, b) => {
      const key = modelSortConfig.key as keyof ModelLoanStats
      const aVal = a[key]
      const bVal = b[key]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return modelSortConfig.direction === 'asc' ? cmp : -cmp
      }

      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      if (aNum === null && bNum === null) return 0
      if (aNum === null) return 1
      if (bNum === null) return -1

      return modelSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [filteredAllModels, modelSortConfig])

  // Column definitions for export (all models tab)
  const modelColumns: ColumnDefinition[] = [
    { key: 'brand', label: t('loan.export.brand') },
    { key: 'model', label: t('loan.export.model') },
    { key: 'total_registrations', label: t('loan.export.registrations'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'vehicles_with_loan', label: t('loan.export.withLoan'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'loan_percentage', label: t('loan.export.loanShare'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) || '0,0' },
  ]

  // Column definitions for export
  const brandColumns: ColumnDefinition[] = [
    { key: 'brand', label: t('loan.export.brand') },
    { key: 'total_registrations', label: t('loan.export.totalRegistrations'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'vehicles_with_loan', label: t('loan.export.withLoan'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'loan_percentage', label: t('loan.export.loanShare'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) || '0,0' },
    { key: 'private_loan_count', label: t('loan.export.privateLoan'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'business_loan_count', label: t('loan.export.businessLoan'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('loan.title')}
        </h1>
<p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('loan.source')}</p>
      </div>


      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={setOutputMode}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        hiddenModes={['month_to_date']}
        maxDataDate={maxLoanDate ?? maxDataDate}
        bottomContent={activeTab !== 'banks' ? (
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        ) : undefined}
      >
        {activeTab === 'banks' && (
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('loan.brandFilterLabel')}
            </label>
            <select
              value={selectedBrands[0] || ''}
              onChange={(e) => {
                setSelectedBrands(e.target.value ? [e.target.value] : [])
              }}
              className="input-field"
            >
              <option value="">{t('loan.allBrands')}</option>
              {availableBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
        )}
      </DateFilterBar>

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('loan.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      {/* <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={OUTPUT_MODE_LABELS[outputMode] || 'Periode'}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      /> */}

      {/* Query Explanation Box */}
      <QueryExplanationBox
        maxDataDate={maxLoanDate ?? maxDataDate}
        maxDataDateLabel={t('loan.maxLoanDataLabel')}
        outputMode={queriedOutputMode}
        vehicleFilter={queriedVehicleFilter}
        postnummerFilter={activeTab !== 'banks' ? queriedPostnummer : undefined}
        periodLabel={periodLabel(queriedOutputMode)}
        startDate={queriedStartDate}
        endDate={queriedEndDate}
        drillDownLabel={queriedSelectedBrand ? t('loan.drillDown', { brand: queriedSelectedBrand }) : null}
        extraWarnings={[
          ...(maxLoanDate && queriedEndDate > maxLoanDate
            ? [t('loan.warnPeriodBeyondLoan', { end: formatNorwegianDate(queriedEndDate), max: formatNorwegianDate(maxLoanDate!) })]
            : []),
          ...(activeTab === 'banks' && queriedSelectedBrands.length > 0 ? [t('loan.filteredByBrand', { brands: queriedSelectedBrands.join(', ') })] : []),
        ]}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => handleTabSwitch('overview')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            {t('loan.tabs.overview')}
          </span>
        </button>
        <button
          onClick={() => handleTabSwitch('brands')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brands'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('loan.tabs.brands')}
          </span>
        </button>
        <button
          onClick={() => handleTabSwitch('models')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'models'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            {t('loan.tabs.models')}
          </span>
        </button>
        <button
          onClick={() => handleTabSwitch('banks')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'banks'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('loan.tabs.banks')}
          </span>
        </button>
      </div>

      {/* Data freshness note — now included in QueryExplanationBox above */}
      {/* <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          <strong>Merk:</strong> Lånedata hentes 15 dager etter bilen er registrert.
        </p>
      </div> */}

      {/* Inline error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('loan.errorPrefix')}</strong> {error}
          </p>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {loading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : overview ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('loan.kpi.totalVehicles')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {(overview.total_vehicles ?? 0).toLocaleString('nb-NO')}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('loan.kpi.withLoan')}</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {(overview.loan_percentage ?? 0).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('loan.kpi.vehicles', { n: (overview.vehicles_with_loan ?? 0).toLocaleString('nb-NO') })}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('loan.kpi.avgAgeWithLoan')}</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {t('loan.kpi.years', { value: overview.avg_owner_age_with_loan != null ? overview.avg_owner_age_with_loan.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : 'N/A' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('loan.kpi.avgAgeWithoutLoan')}</p>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {t('loan.kpi.years', { value: overview.avg_owner_age_without_loan != null ? overview.avg_owner_age_without_loan.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : 'N/A' })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Loan Chart */}
          {weeklyData.length > 0 && (
            <LoanWeeklyChart data={weeklyData} prevYearData={prevYearWeeklyData} year={parseInt(selectedYear)} />
          )}
            </>
          ) : (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('loan.noLoanData')}
            </div>
          )}
        </>
      )}

      {/* Brands Tab */}
      {activeTab === 'brands' && (
        <>
          {loading && !selectedBrand ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : brands.length === 0 && !selectedBrand ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('loan.noData')}
            </div>
          ) : selectedBrand ? (
            /* ── Model drill-down for selected brand ── */
            <>
              {/* Breadcrumb + back */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={handleBackToBrands}
                  className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>{t('loan.back')}</span>
                </button>
                <span className="text-gray-400 dark:text-gray-500">/</span>
                <span className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 cursor-pointer" onClick={handleBackToBrands}>{t('loan.breadcrumb')}</span>
                <ChevronRight className="w-3 h-3 text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedBrand}</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('loan.brandModelsTitle', { brand: selectedBrand })}
              </h2>

              {modelsLoading ? (
                <div className="card p-8 text-center">
                  <LoadingSpinner />
                </div>
              ) : models.length === 0 ? (
                <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
                  {t('loan.noModelData', { brand: selectedBrand })}
                </div>
              ) : (
                <>
                  <div className="flex justify-end items-center mb-4">
                    <ExportButtons
                      data={sortedBrandModels}
                      columns={modelColumns}
                      metadata={{
                        title: t('loan.export.titleBrandModels', { brand: selectedBrand }),
                        dateRange: `${formatNorwegianDate(queriedStartDate)} - ${formatNorwegianDate(queriedEndDate)}`,
                        filters: minePostnummer ? [t('loan.export.filterPostnummer', { value: minePostnummer })] : undefined,
                      }}
                    />
                  </div>
                  <div className="mb-4">
                    <SearchBar value={brandModelsSearch} onChange={setBrandModelsSearch} placeholder={t('loan.searchModel')} />
                  </div>
                  <div className="card overflow-x-auto">
                    <table className="data-table data-table-compact">
                      <thead>
                        <tr>
                          <th className="text-center w-12">#</th>
                          <th
                            className="w-40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => handleBrandModelSort('model')}
                          >
                            <div className="flex items-center justify-between">
                              <span>{t('loan.cols.model')}</span>
                              {getBrandModelSortIcon('model')}
                            </div>
                          </th>
                          <th
                            className="text-right w-32 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => handleBrandModelSort('total_registrations')}
                          >
                            <div className="flex items-center justify-end">
                              <span>{t('loan.cols.registrations')}</span>
                              {getBrandModelSortIcon('total_registrations')}
                            </div>
                          </th>
                          <th
                            className="text-right w-24 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => handleBrandModelSort('vehicles_with_loan')}
                          >
                            <div className="flex items-center justify-end">
                              <span>{t('loan.cols.withLoan')}</span>
                              {getBrandModelSortIcon('vehicles_with_loan')}
                            </div>
                          </th>
                          <th
                            className="text-right w-28 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600"
                            onClick={() => handleBrandModelSort('loan_percentage')}
                          >
                            <div className="flex items-center justify-end">
                              <span>{t('loan.cols.loanShare')}</span>
                              {getBrandModelSortIcon('loan_percentage')}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandModelsSummary && (
                          <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                            <td className="text-center"><span className="text-primary-600 dark:text-primary-400 font-bold">&sum;</span></td>
                            <td className="font-bold text-primary-700 dark:text-primary-300 py-2">TOTAL</td>
                            <td className="text-right font-bold">{brandModelsSummary.total_registrations.toLocaleString('nb-NO')}</td>
                            <td className="text-right font-bold">{brandModelsSummary.vehicles_with_loan.toLocaleString('nb-NO')}</td>
                            <td className="text-right font-bold border-r-2 border-gray-300 dark:border-gray-600">{brandModelsSummary.loan_percentage.toFixed(1).replace('.', ',')} %</td>
                          </tr>
                        )}
                        {sortedBrandModels.map((model, index) => (
                          <tr key={`${model.brand}-${model.model}`} className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}>
                            <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                            <td className="font-semibold">{model.model}</td>
                            <td className="text-right">{model.total_registrations.toLocaleString('nb-NO')}</td>
                            <td className="text-right">{model.vehicles_with_loan.toLocaleString('nb-NO')}</td>
                            <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                              <span className="text-gray-900 dark:text-gray-100">
                                {formatDecimal(model.loan_percentage)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {sortedBrandModels.length === 0 && brandModelsSearch && (
                          <tr>
                            <td colSpan={5} className="text-center text-gray-500 dark:text-gray-400 py-8">
                              {t('loan.noModelsFoundQuoted', { term: brandModelsSearch })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          ) : (
            /* ── Brand list (top level) ── */
            <>
              <div className="flex justify-end items-center mb-4">
                <ExportButtons
                  data={sortedBrands}
                  columns={brandColumns}
                  metadata={{
                    title: t('loan.export.titleBrands'),
                    dateRange: `${formatNorwegianDate(queriedStartDate)} - ${formatNorwegianDate(queriedEndDate)}`,
                    filters: minePostnummer ? [t('loan.export.filterPostnummer', { value: minePostnummer })] : undefined,
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: periodLabel(queriedOutputMode),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                      drillDownLabel: null,
                      extraWarnings: [
                        ...(maxLoanDate && queriedEndDate > maxLoanDate
                          ? [t('loan.exportWarnPeriodBeyondLoan', { end: formatNorwegianDate(queriedEndDate), max: formatNorwegianDate(maxLoanDate!) })]
                          : []),
                      ],
                    }),
                  }}
                />
              </div>

            <div className="mb-4">
              <SearchBar value={brandSearchTerm} onChange={setBrandSearchTerm} placeholder={t('loan.searchBrand')} />
            </div>
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-center w-12">#</th>
                  <th
                    className="w-40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleBrandSort('brand')}
                  >
                    <div className="flex items-center justify-between">
                      <span>{t('loan.cols.brand')}</span>
                      {getBrandSortIcon('brand')}
                    </div>
                  </th>
                  <th
                    className="text-right w-32 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleBrandSort('total_registrations')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('loan.cols.registrations')}</span>
                      {getBrandSortIcon('total_registrations')}
                    </div>
                  </th>
                  <th
                    className="text-right w-24 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleBrandSort('vehicles_with_loan')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('loan.cols.withLoan')}</span>
                      {getBrandSortIcon('vehicles_with_loan')}
                    </div>
                  </th>
                  <th
                    className="text-right w-28 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600"
                    onClick={() => handleBrandSort('loan_percentage')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('loan.cols.loanShare')}</span>
                      {getBrandSortIcon('loan_percentage')}
                    </div>
                  </th>
                  <th
                    className="text-right w-28 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleBrandSort('private_loan_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('loan.cols.privateLoan')}</span>
                      {getBrandSortIcon('private_loan_count')}
                    </div>
                  </th>
                  <th
                    className="text-right w-28 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleBrandSort('business_loan_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('loan.cols.businessLoan')}</span>
                      {getBrandSortIcon('business_loan_count')}
                    </div>
                  </th>
                  <th className="text-center w-28">{t('loan.cols.action')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Total row from server summary */}
                {brandsSummary && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                    <td className="text-center"><span className="text-primary-600 dark:text-primary-400 font-bold">&sum;</span></td>
                    <td className="font-bold text-primary-700 dark:text-primary-300 py-2">TOTAL</td>
                    <td className="text-right font-bold">{(brandsSummary.total_registrations ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-right font-bold">{(brandsSummary.vehicles_with_loan ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-right font-bold border-r-2 border-gray-300 dark:border-gray-600">{(brandsSummary.loan_percentage ?? 0).toFixed(1).replace('.', ',')} %</td>
                    <td className="text-right font-bold">{(brandsSummary.private_loan_count ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-right font-bold">{(brandsSummary.business_loan_count ?? 0).toLocaleString('nb-NO')}</td>
                    <td></td>
                  </tr>
                )}
                {filteredBrands.map((brand, index) => (
                  <tr
                    key={brand.brand}
                    className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${activeMerker.has(brand.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                  >
                    <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                    <td className="font-semibold">{brand.brand}</td>
                    <td className="text-right">{brand.total_registrations.toLocaleString('nb-NO')}</td>
                    <td className="text-right">{brand.vehicles_with_loan.toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-gray-100">
                        {formatDecimal(brand.loan_percentage)}
                      </span>
                    </td>
                    <td className="text-right">{(brand.private_loan_count ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-right">{(brand.business_loan_count ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-center">
                      <button
                        onClick={() => handleBrandClick(brand.brand)}
                        className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                      >
                        {t('loan.cols.modelsButton')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            </>
          )}
        </>
      )}

      {/* Models Tab */}
      {activeTab === 'models' && (
        <>
          {allModelsError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>{t('loan.errorPrefix')}</strong> {allModelsError}
              </p>
            </div>
          )}
          {allModelsLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : sortedAllModels.length === 0 && !allModelsSearch ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('loan.noData')}
            </div>
          ) : (
            <>
              {/* Export and Clear Sorting */}
              <div className="flex justify-end items-center mb-4">
                <ExportButtons
                  data={sortedAllModels}
                  columns={modelColumns}
                  metadata={{
                    title: t('loan.export.titleModels'),
                    dateRange: `${formatNorwegianDate(queriedStartDate)} - ${formatNorwegianDate(queriedEndDate)}`,
                    filters: minePostnummer ? [t('loan.export.filterPostnummer', { value: minePostnummer })] : undefined,
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: periodLabel(queriedOutputMode),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                      drillDownLabel: null,
                      extraWarnings: [
                        ...(maxLoanDate && queriedEndDate > maxLoanDate
                          ? [t('loan.exportWarnPeriodBeyondLoan', { end: formatNorwegianDate(queriedEndDate), max: formatNorwegianDate(maxLoanDate!) })]
                          : []),
                      ],
                    }),
                  }}
                />
              </div>

              <div className="mb-4">
                <SearchBar value={allModelsSearch} onChange={setAllModelsSearch} placeholder={t('loan.searchBrandModel')} />
              </div>
              <div className="card overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-center w-12">#</th>
                      <th
                        className="w-32 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleModelSort('brand')}
                      >
                        <div className="flex items-center justify-between">
                          <span>{t('loan.cols.brand')}</span>
                          {getModelSortIcon('brand')}
                        </div>
                      </th>
                      <th
                        className="w-40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleModelSort('model')}
                      >
                        <div className="flex items-center justify-between">
                          <span>{t('loan.cols.model')}</span>
                          {getModelSortIcon('model')}
                        </div>
                      </th>
                      <th
                        className="text-right w-32 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleModelSort('total_registrations')}
                      >
                        <div className="flex items-center justify-end">
                          <span>{t('loan.cols.registrations')}</span>
                          {getModelSortIcon('total_registrations')}
                        </div>
                      </th>
                      <th
                        className="text-right w-24 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleModelSort('vehicles_with_loan')}
                      >
                        <div className="flex items-center justify-end">
                          <span>{t('loan.cols.withLoan')}</span>
                          {getModelSortIcon('vehicles_with_loan')}
                        </div>
                      </th>
                      <th
                        className="text-right w-28 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600"
                        onClick={() => handleModelSort('loan_percentage')}
                      >
                        <div className="flex items-center justify-end">
                          <span>{t('loan.cols.loanShare')}</span>
                          {getModelSortIcon('loan_percentage')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAllModels.map((model, index) => (
                      <tr key={`${model.brand}-${model.model}`} className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}>
                        <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                        <td className="font-semibold">{model.brand}</td>
                        <td>{model.model}</td>
                        <td className="text-right">{model.total_registrations.toLocaleString('nb-NO')}</td>
                        <td className="text-right">{model.vehicles_with_loan.toLocaleString('nb-NO')}</td>
                        <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                          <span className="text-gray-900 dark:text-gray-100">
                            {formatDecimal(model.loan_percentage)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {sortedAllModels.length === 0 && allModelsSearch && (
                      <tr>
                        <td colSpan={6} className="text-center text-gray-500 dark:text-gray-400 py-8">
                          {t('loan.noModelsFound', { term: allModelsSearch })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Banks Tab */}
      {activeTab === 'banks' && (
        <>
          {loading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : banks.length === 0 ? (
            <div className="card p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-2">{t('loan.noData')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {selectedBrands.length > 0 
                  ? t('loan.banks.noneForBrands', { brands: selectedBrands.join(', ') })
                  : t('loan.banks.chooseBrands')
                }
              </p>
            </div>
          ) : (
          <>
          {/* Market Share Pie Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('loan.banks.marketShareTitle')}{selectedBrands.length > 0 ? ` ${selectedBrands.join(', ')}` : ''}
              </h2>
              <div className="h-80">
                <Pie
                  data={{
                    labels: (() => {
                      const top5 = banks.slice(0, 5)
                      const labels = top5.map((bank) => formatLeasingselskap(bank.bank))
                      const top5Sum = top5.reduce((sum, bank) => sum + toNumber(bank.market_share), 0)
                      const otherShare = 100 - top5Sum
                      if (otherShare > 0) {
                        labels.push(t('loan.banks.other'))
                      }
                      return labels
                    })(),
                    datasets: [
                      {
                        data: (() => {
                          const top5 = banks.slice(0, 5)
                          const data = top5.map((bank) => bank.market_share)
                          const top5Sum = top5.reduce((sum, bank) => sum + toNumber(bank.market_share), 0)
                          const otherShare = 100 - top5Sum
                          if (otherShare > 0) {
                            data.push(otherShare)
                          }
                          return data
                        })(),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.7)',
                          'rgba(16, 185, 129, 0.7)',
                          'rgba(245, 158, 11, 0.7)',
                          'rgba(239, 68, 68, 0.7)',
                          'rgba(168, 85, 247, 0.7)',
                          'rgba(156, 163, 175, 0.7)', // Grey for "Andre"
                        ],
                        borderWidth: 2,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Top Banks Summary */}
            <div className="card p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('loan.banks.top5Title')}{selectedBrands.length > 0 ? ` ${selectedBrands.join(', ')}` : ''}
              </h2>
              <div className="space-y-4">
                {banks.slice(0, 5).map((bank, index) => (
                  <div key={bank.bank} className="border-b border-gray-200 dark:border-dark-border pb-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          #{index + 1}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-gray-100">
                            {formatLeasingselskap(bank.bank)}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('loan.banks.loans', { n: bank.total_loans.toLocaleString('nb-NO') })}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                        {formatDecimal(bank.market_share)}%
                      </span>
                    </div>
                    {/* One selected brand makes the breakdown that brand at 100 % (#406) */}
                    {selectedBrands.length !== 1 && bank.top_brands && bank.top_brands.length > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t('loan.banks.topBrands', { brands: bank.top_brands.map((b) => b.brand).join(', ') })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Banks Table with Brand Market Share - only show when no brand is selected */}
          {selectedBrands.length === 0 && (
          <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('loan.banks.colBank')}</th>
                    <th className="text-right">{t('loan.banks.colTotalLoans')}</th>
                    <th className="text-right">{t('loan.banks.colMarketShare')}</th>
                    <th>{t('loan.banks.colBrandsShare')}</th>
                  </tr>
                </thead>
                <tbody>
                  {banks.map((bank, index) => (
                    <tr key={bank.bank}>
                      <td className="text-center text-gray-700 dark:text-gray-300">
                        {index + 1}
                      </td>
                      <td className="font-semibold">{formatLeasingselskap(bank.bank)}</td>
                      <td className="text-right">{bank.total_loans.toLocaleString('nb-NO')}</td>
                      <td className="text-right font-semibold">
                        {formatDecimal(bank.market_share, 2)}%
                      </td>
                      <td>
                        {bank.top_brands && bank.top_brands.length > 0 ? (
                          <div className="space-y-1">
                            {bank.top_brands.map((brand) => {
                              const brandShare = (brand.count / bank.total_loans * 100)
                              return (
                                <div key={brand.brand} className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-sm font-medium">
                                    {brand.brand}
                                  </span>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {t('loan.banks.loans', { n: brand.count.toLocaleString('nb-NO') })}
                                  </span>
                                  <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                                    {t('loan.banks.shareOfBank', { share: brandShare.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) })}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </>
      )}
    </div>
  )
}
